export const AIMETA_PLATFORM_DEV_DEBUG = 'dev_debug';

/** 是否为 SDK 开发调试平台（无需扫码，默认已登录） */
export function isAimetaDevDebugPlatform(platform?: string | null): boolean {
  const key = String(platform ?? '').trim();
  return key === AIMETA_PLATFORM_DEV_DEBUG || key === 'DEV_DEBUG' || key === '开发调试';
}

/** 开发调试平台默认会话信息 */
export function buildAimetaDevDebugSessionProfile(): {
  nickname: string;
  finderUsername: string;
} {
  return { nickname: '开发调试', finderUsername: 'dev_debug' };
}

/** 解析 AimetaPlatform 宿主桥返回（JSON 字符串或对象） */
export function parseAimetaPlatformBridgeResponse(raw: unknown): Record<string, any> {
  if (raw == null || raw === '') {
    return { rspCode: 1, rspMsg: 'native bridge empty response' };
  }
  if (typeof raw === 'object') {
    return raw as Record<string, any>;
  }
  try {
    return JSON.parse(String(raw)) as Record<string, any>;
  } catch {
    return { rspCode: 1, rspMsg: 'native bridge invalid JSON' };
  }
}

function isSessionPayloadLoggedIn(
  payload: Record<string, any> | null | undefined
): boolean {
  if (!payload) return false;
  if (payload.ok === true || payload.ok === 1) return true;
  const finder = String(
    payload.finderUsername ?? payload.finder_username ?? ''
  ).trim();
  const nickname = String(payload.nickname ?? '').trim();
  return !!(finder || nickname);
}

/** 判断 GetSession / GetPlatformStatus 是否表示已登录 */
export function isAimetaPlatformSessionLoggedIn(
  res: Record<string, any> | null | undefined
): boolean {
  if (!res) return false;
  const data = (res.data ?? res.Data ?? res) as Record<string, any>;
  if (data?.bound === true || data?.bound === 1) return true;
  const session = (data?.session ?? data?.Session) as
    | Record<string, any>
    | undefined;
  if (isSessionPayloadLoggedIn(session)) return true;
  const invokePayload = extractInvokePayload(data);
  if (isSessionPayloadLoggedIn(invokePayload)) return true;
  const nested = invokePayload?.payload as Record<string, any> | undefined;
  if (isSessionPayloadLoggedIn(nested)) return true;
  return false;
}

/** 从 GetSession / GetPlatformStatus 响应中提取登录账号信息 */
export function extractAimetaPlatformSessionProfile(
  res: Record<string, any> | null | undefined
): { nickname: string; finderUsername: string } {
  if (!res) return { nickname: '', finderUsername: '' };
  const data = (res.data ?? res.Data ?? res) as Record<string, any>;
  const candidates = [
    data?.session,
    data?.Session,
    extractInvokePayload(data),
    extractInvokePayload(data)?.payload,
  ];
  for (const item of candidates) {
    const node = (item ?? {}) as Record<string, any>;
  const nickname = String(
    node?.nickname ?? node?.author ?? ''
  ).trim();
  const finderUsername = String(
    node?.finderUsername ?? node?.finder_username ?? ''
  ).trim();
    if (nickname || finderUsername) {
      return { nickname, finderUsername };
    }
  }
  return { nickname: '', finderUsername: '' };
}

/** 展示用主播名称：优先 nickname，否则 finderUsername */
export function formatAimetaPlatformAnchorName(
  profile: { nickname: string; finderUsername: string } | null | undefined
): string {
  if (!profile) return '';
  return profile.nickname || profile.finderUsername || '';
}

export type AimetaPlatformInfoRow = { label: string; value: string };

export type AimetaPlatformLiveEventItem = {
  user: string;
  content: string;
  time: string;
};

export type AimetaPlatformOnlineUserItem = {
  nickname: string;
  extra?: string;
};

export type AimetaPlatformLiveInfoDetail = {
  overview: AimetaPlatformInfoRow[];
  comments: AimetaPlatformLiveEventItem[];
  joins: AimetaPlatformLiveEventItem[];
  gifts: AimetaPlatformLiveEventItem[];
  likes: AimetaPlatformLiveEventItem[];
  onlineCount: number;
  peakOnlineCount?: number;
  onlineUsers: AimetaPlatformOnlineUserItem[];
};

export type AimetaPlatformLiveInfoTabKey =
  | 'overview'
  | 'comments'
  | 'joins'
  | 'gifts'
  | 'likes'
  | 'online';

/** SDK GetLiveStatus：true=直播中，false=未开播，null=无状态 */
export function isAimetaPlatformSdkLiving(
  raw: Record<string, any> | null | undefined
): boolean | null {
  if (!raw) return null;
  const data = (raw.data ?? raw) as Record<string, any>;
  const status = extractInvokePayload(data.status as Record<string, any>);
  return parseAimetaPlatformSdkLivingFlag(status.isLiving);
}

function parseAimetaPlatformSdkLivingFlag(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function formatAimetaPlatformSdkLivingLabel(value: unknown): string {
  const living = parseAimetaPlatformSdkLivingFlag(value);
  if (living === true) return '直播中';
  if (living === false) return '未开播';
  return '';
}

function extractInvokePayload(res: Record<string, any> | null | undefined): Record<string, any> {
  if (!res) return {};
  const data = (res.data ?? res.Data ?? res) as Record<string, any>;
  const payload = (data.payload ?? data) as Record<string, any>;
  if (payload?.payload && typeof payload.payload === 'object') {
    return payload.payload as Record<string, any>;
  }
  return payload;
}

function pushRow(
  rows: AimetaPlatformInfoRow[],
  label: string,
  value: unknown
) {
  if (value == null || value === '') return;
  rows.push({ label, value: String(value) });
}

/** 概览区仅展示：平台、主播、直播状态 */
export function buildAimetaPlatformLiveInfoOverviewRows(
  raw: Record<string, any> | null | undefined,
  extra?: { anchorName?: string; platformLabel?: string }
): AimetaPlatformInfoRow[] {
  const rows: AimetaPlatformInfoRow[] = [];
  if (extra?.platformLabel) {
    pushRow(rows, '平台', extra.platformLabel);
  }
  const anchor =
    extra?.anchorName ||
    (() => {
      if (!raw) return '';
      const data = (raw.data ?? raw) as Record<string, any>;
      const status = extractInvokePayload(data.status as Record<string, any>);
      const room = extractInvokePayload(data.room as Record<string, any>);
      return String(
        status.nickname ?? room.nickname ?? room.author ?? status.author ?? ''
      ).trim();
    })();
  if (anchor) {
    pushRow(rows, '主播', anchor);
  }
  if (raw) {
    const data = (raw.data ?? raw) as Record<string, any>;
    const status = extractInvokePayload(data.status as Record<string, any>);
    const living = formatAimetaPlatformSdkLivingLabel(status.isLiving);
    if (living) {
      pushRow(rows, '直播状态', living);
    }
  }
  return rows;
}

/** 将 getAimetaPlatformLiveInfo 返回值格式化为展示行（调试/全量用，面板概览请用 overviewRows） */
export function buildAimetaPlatformLiveInfoRows(
  raw: Record<string, any> | null | undefined,
  extra?: { anchorName?: string; platformLabel?: string }
): AimetaPlatformInfoRow[] {
  return buildAimetaPlatformLiveInfoOverviewRows(raw, extra);
}

function formatEventTime(ts: unknown): string {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return '';
  const d = new Date(n);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function parseLiveEventList(
  list: unknown,
  fallbackContent = ''
): AimetaPlatformLiveEventItem[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      const row = (item ?? {}) as Record<string, any>;
      const user = String(row.user ?? row.nickname ?? row.name ?? '用户').trim();
      const content = String(
        row.content ?? row.text ?? row.message ?? fallbackContent
      ).trim();
      if (!user && !content) return null;
      return {
        user: user || '用户',
        content: content || fallbackContent,
        time: formatEventTime(row.ts ?? row.time ?? row.timestamp),
      };
    })
    .filter((item): item is AimetaPlatformLiveEventItem => item != null);
}

function extractOnlineUsers(
  online: Record<string, any>
): AimetaPlatformOnlineUserItem[] {
  let users = Array.isArray(online.users)
    ? online.users
    : Array.isArray(online.list)
      ? online.list
      : [];
  if (!users.length && Array.isArray(online.members)) {
    users = online.members;
  }
  return users
    .map((item) => {
      if (typeof item === 'string') {
        const nickname = item.trim();
        return nickname ? { nickname } : null;
      }
      const row = (item ?? {}) as Record<string, any>;
      const nickname = String(
        row.nickname ?? row.name ?? row.user ?? row.username ?? ''
      ).trim();
      if (!nickname) return null;
      const extra = String(row.level ?? row.badge ?? row.tag ?? '').trim();
      return { nickname, extra: extra || undefined };
    })
    .filter((item): item is AimetaPlatformOnlineUserItem => item != null);
}

export type PlatformSdkPushDetail = {
  platform?: string;
  event?: string;
  payload?: Record<string, any>;
  /** 实时推送：仅最新一条互动事件（增量） */
  liveEventDelta?: Record<string, any>;
  /** 全量快照：仅 GetLiveEvents / 主动拉取时使用 */
  liveEvents?: Record<string, any>;
  online?: Record<string, any>;
  status?: Record<string, any>;
  stats?: Record<string, any>;
};

function wrapSdkSectionPayload(payload: Record<string, any>): Record<string, any> {
  return {
    rspCode: 0,
    data: {
      requestId: '',
      code: 'OK',
      message: '',
      payload,
    },
  };
}

function normalizeOnlineSectionPayload(
  online?: Record<string, any> | null
): Record<string, any> | null {
  if (!online) return null;
  const payload = { ...online };
  if (!Array.isArray(payload.users) || !payload.users.length) {
    if (Array.isArray(payload.members) && payload.members.length) {
      payload.users = payload.members
        .map((member: unknown) => {
          if (typeof member === 'string') return member;
          const row = (member ?? {}) as Record<string, any>;
          return String(row.name ?? row.nickname ?? row.user ?? '').trim();
        })
        .filter(Boolean);
    }
  }
  return payload;
}

function appendLiveEventItem(
  list: Record<string, any>[],
  item: Record<string, any> | null | undefined
): Record<string, any>[] {
  if (!item) return list;
  const user = String(item.user ?? item.nickname ?? item.name ?? '').trim();
  const content = String(
    item.content ?? item.text ?? item.message ?? ''
  ).trim();
  if (!user && !content) return list;
  const row = {
    user: user || '用户',
    content,
    ts: item.ts ?? item.time ?? item.timestamp ?? Date.now(),
  };
  const key = `${row.user}|${row.content}`;
  if (
    list.some(
      (x) => `${String(x.user ?? '').trim()}|${String(x.content ?? '').trim()}` === key
    )
  ) {
    return list;
  }
  return [row, ...list].slice(0, 200);
}

function dedupeLiveEventRows(list: Record<string, any>[]): Record<string, any>[] {
  const seen = new Set<string>();
  const out: Record<string, any>[] = [];
  for (const item of list) {
    const user = String(item.user ?? item.nickname ?? item.name ?? '').trim();
    const content = String(item.content ?? item.text ?? item.message ?? '').trim();
    const key = `${user}|${content}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function mergeLiveEventsSection(
  prev: Record<string, any> | undefined,
  push: PlatformSdkPushDetail
): Record<string, any> {
  const prevPayload = extractInvokePayload(prev);
  let comments = Array.isArray(prevPayload.comments)
    ? [...prevPayload.comments]
    : [];
  let joins = Array.isArray(prevPayload.joins) ? [...prevPayload.joins] : [];
  let gifts = Array.isArray(prevPayload.gifts) ? [...prevPayload.gifts] : [];
  let likes = Array.isArray(prevPayload.likes) ? [...prevPayload.likes] : [];

  if (push.liveEventDelta && typeof push.liveEventDelta === 'object') {
    const delta = push.liveEventDelta as Record<string, any>;
    const item = {
      user: delta.user ?? delta.nickname,
      content: delta.content ?? delta.giftName,
      ts: delta.ts ?? Date.now(),
    };
    switch (String(delta.type ?? '')) {
      case 'comment':
        comments = appendLiveEventItem(comments, item);
        break;
      case 'join':
        joins = appendLiveEventItem(joins, item);
        break;
      case 'gift':
        gifts = appendLiveEventItem(gifts, item);
        break;
      case 'like':
        likes = appendLiveEventItem(likes, item);
        break;
      default:
        break;
    }
    return wrapSdkSectionPayload({ ok: true, comments, joins, gifts, likes });
  }

  if (push.liveEvents && typeof push.liveEvents === 'object') {
    const snap = push.liveEvents as Record<string, any>;
    if (Array.isArray(snap.comments)) {
      comments = dedupeLiveEventRows(parseLiveEventList(snap.comments, '发表评论'));
    }
    if (Array.isArray(snap.joins)) {
      joins = dedupeLiveEventRows(parseLiveEventList(snap.joins, '进入直播间'));
    }
    if (Array.isArray(snap.gifts)) {
      gifts = dedupeLiveEventRows(parseLiveEventList(snap.gifts, '送礼物'));
    }
    if (Array.isArray(snap.likes)) {
      likes = dedupeLiveEventRows(parseLiveEventList(snap.likes, '为你点赞了'));
    }
    return wrapSdkSectionPayload({ ok: true, comments, joins, gifts, likes });
  }

  const ev = String(push.event ?? '');
  const p = (push.payload ?? {}) as Record<string, any>;
  if (ev === 'live.comment.new' || ev === 'comment' || ev === 'onComment') {
    comments = appendLiveEventItem(comments, {
      user: p.nickname ?? p.user,
      content: p.content ?? p.text,
      ts: Date.now(),
    });
  } else if (
    ev === 'live.viewer.new' ||
    ev === 'onNewViewer' ||
    ev === 'enter' ||
    ev === 'newViewer'
  ) {
    joins = appendLiveEventItem(joins, {
      user: p.nickname ?? p.user,
      content: p.content ?? '进入直播间',
      ts: Date.now(),
    });
  } else if (ev === 'live.gift.new' || ev === 'onGift' || ev === 'gift') {
    gifts = appendLiveEventItem(gifts, {
      user: p.nickname ?? p.user,
      content: p.giftName ?? p.gift ?? p.content ?? '送礼物',
      ts: Date.now(),
    });
  } else if (ev === 'live.like.new' || ev === 'onLike' || ev === 'like') {
    likes = appendLiveEventItem(likes, {
      user: p.nickname ?? p.user,
      content: p.content ?? '为你点赞了',
      ts: Date.now(),
    });
  }

  return wrapSdkSectionPayload({ ok: true, comments, joins, gifts, likes });
}

/** 将 Android 推送的 SDK 事件合并进平台信息（事件驱动，不做全量 RPC） */
export function mergePlatformSdkPushIntoLiveInfo(
  raw: Record<string, any> | null | undefined,
  push: PlatformSdkPushDetail
): Record<string, any> {
  const base = raw?.data ? { ...raw } : { rspCode: 0, data: {} as Record<string, any> };
  const data = { ...(base.data as Record<string, any>) };

  data.events = mergeLiveEventsSection(data.events as Record<string, any>, push);

  const online = normalizeOnlineSectionPayload(push.online);
  if (
    online &&
    (push.event === 'onOnlineUsers' ||
      push.event === 'live.online.changed' ||
      push.online)
  ) {
    data.online = wrapSdkSectionPayload(online);
  }

  const statusPayload =
    push.status ??
    (push.event === 'onLiveStatus' || push.event === 'live.status.changed'
      ? push.payload
      : null);
  if (statusPayload) data.status = wrapSdkSectionPayload(statusPayload);

  const statsPayload =
    push.stats ??
    (push.event === 'onLiveStats' || push.event === 'live.stats.updated'
      ? push.payload
      : null);
  if (statsPayload) data.stats = wrapSdkSectionPayload(statsPayload);

  if (
    (push.event === 'auth.login.success' ||
      push.event === 'live.room.info' ||
      push.event === 'live.author.changed') &&
    push.payload
  ) {
    data.room = wrapSdkSectionPayload(push.payload);
    const author = String(
      (push.payload as Record<string, any>).author ??
        (push.payload as Record<string, any>).nickname ??
        ''
    ).trim();
    if (author) {
      const statusPayload = extractInvokePayload(data.status as Record<string, any>);
      data.status = wrapSdkSectionPayload({
        ...statusPayload,
        nickname: author,
        author,
      });
    }
  }

  return { ...base, data, sdkPushAt: Date.now() };
}

/** 将 getAimetaPlatformLiveInfo 返回值格式化为选项卡详情 */
export function buildAimetaPlatformLiveInfoDetail(
  raw: Record<string, any> | null | undefined,
  extra?: { anchorName?: string; platformLabel?: string }
): AimetaPlatformLiveInfoDetail {
  const overview = buildAimetaPlatformLiveInfoOverviewRows(raw, extra);
  if (!raw) {
    return {
      overview,
      comments: [],
      joins: [],
      gifts: [],
      likes: [],
      onlineCount: 0,
      onlineUsers: [],
    };
  }
  const data = (raw.data ?? raw) as Record<string, any>;
  const stats = extractInvokePayload(data.stats as Record<string, any>);
  const online = extractInvokePayload(data.online as Record<string, any>);
  const events = extractInvokePayload(data.events as Record<string, any>);
  const metrics = (stats.metrics ?? stats) as Record<string, any>;
  const onlineUsers = extractOnlineUsers(online);
  const onlineCount = Number(
    online.count ?? online.onlineCount ?? onlineUsers.length ?? 0
  );
  const peakOnlineCount = Number(metrics.peakOnlineCount ?? 0) || undefined;

  return {
    overview,
    comments: parseLiveEventList(events.comments, '发表评论'),
    joins: parseLiveEventList(events.joins, '进入直播间'),
    gifts: parseLiveEventList(events.gifts, '送礼物'),
    likes: parseLiveEventList(events.likes, '为你点赞了'),
    onlineCount: Number.isFinite(onlineCount) ? onlineCount : onlineUsers.length,
    peakOnlineCount,
    onlineUsers,
  };
}

/** 从 GetLoginQr 响应中提取二维码 data URL */
export function extractAimetaPlatformQrBase64(res: Record<string, any>): string {
  const candidates = [
    res?.data?.payload?.payload,
    res?.data?.payload,
    res?.data,
    res?.payload?.payload,
    res?.payload,
    res,
  ];
  for (const node of candidates) {
    const qr = pickQrFromNode(node);
    if (qr) return qr;
  }
  return '';
}

function pickQrFromNode(node: unknown): string {
  if (node == null) return '';
  let obj: Record<string, any> | null = null;
  if (typeof node === 'string') {
    const trimmed = node.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('data:image')) return trimmed;
    try {
      obj = JSON.parse(trimmed) as Record<string, any>;
    } catch {
      return trimmed.startsWith('iVBOR') ? `data:image/png;base64,${trimmed}` : '';
    }
  } else if (typeof node === 'object') {
    obj = node as Record<string, any>;
  }
  if (!obj) return '';
  const raw =
    obj.imageBase64 ||
    obj.imageUrl ||
    obj.qrBase64 ||
    obj.qrImageBase64 ||
    obj.base64 ||
    obj.qr ||
    obj.qrCode ||
    obj.image ||
    '';
  const s = String(raw || '').trim();
  if (!s) {
    const nested = obj.payload ?? obj.data ?? obj.result;
    if (nested && nested !== obj) return pickQrFromNode(nested);
    return '';
  }
  if (s.startsWith('data:')) return s;
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  return `data:image/png;base64,${s}`;
}
